import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import moment from "moment";

import ACTION from "../../store/action";
import { selectUser, selectFiltersModified } from "../../store/selector";
import { selectedViews } from "../../constant/table";
import SuccessMessage from "../../component/customComponent/customMessages/SuccessMessage";
import ErrorMessage from "../../component/customComponent/customMessages/ErrorMessage";
import { useInlineEditTracking } from "../../component/TableSection/InlineEdit/useInlineEditTracking";
import { InlineEditStatus } from "../../utils/pendo/useStandardizedTracking";
import { updateClaimInlineAPI, updateEnrollmentInlineAPI } from "../../api/api";
import { PAID, DENIED, SUBMITTED, NEW_PENDING_PAYMENT } from "../../constant/claim";

const toIso = (value) => {
  if (value == null) return null;
  return moment.isMoment(value) ? value.toISOString() : value;
};

const toNumber = (value) => (value == null ? null : Number(value));

// Claim-form parity (PaymentInformationControls validators): the fields a target status requires to
// save. paidAmount/deniedAmount must be > 0; receivedDate is the form's "Denied/Received date".
const REQUIRED_CLAIM_FIELDS_BY_STATUS = {
  [NEW_PENDING_PAYMENT]: ["paidAmount"],
  [PAID]: ["paidAmount", "receivedDate"],
  [DENIED]: ["deniedAmount", "receivedDate"]
};

// `plan` carries this commit's auto-fills, so a field counts as present when the plan will set it.
const getMissingClaimFields = (status, record, plan = {}) =>
  (REQUIRED_CLAIM_FIELDS_BY_STATUS[status] || []).filter((field) => {
    if (field === "receivedDate") return !(plan?.patch?.claimReceivedDate || record.claimReceivedDate);
    if (field === "paidAmount") {
      const amount = plan?.patch?.claimPaidAmount ?? record.claimPaidAmount;
      return amount == null || Number(amount) <= 0;
    }
    if (field === "deniedAmount") {
      const amount = plan?.patch?.claimDeniedAmount ?? record.claimDeniedAmount;
      return amount == null || Number(amount) <= 0;
    }
    return false;
  });

const buildPlanForClaim = (record, columnKey, newValue, lookups = {}) => {
  const { assignees = [], facilities = [] } = lookups;

  switch (columnKey) {
    case "claimDate": {
      const iso = toIso(newValue);
      return { fields: { claimDate: iso }, patch: { claimDate: iso } };
    }
    case "claimStatus": {
      const fields = { status: newValue };
      const patch = { claimStatus: newValue };
      const today = toIso(moment());
      const totalAmount = toNumber(record.claimTotalAmount) || 0;
      // Claim-form parity (claimDataChanged): the total is only defaulted into the target amount for
      // a claim with NO prior amount. A claim that's already paid/denied keeps its amounts.
      const hasExistingAmount = Boolean(record.claimPaidAmount) || Boolean(record.claimDeniedAmount);

      // Claim-form parity — date auto-fills (DateAndInfoControls.handleChangeSelect): each status
      // stamps its date with today when unset.
      if (newValue === SUBMITTED && !record.claimSubmittedDate) {
        fields.submittedDate = today;
        patch.claimSubmittedDate = today;
      }
      if (newValue === PAID && !record.claimReceivedDate) {
        fields.receivedDate = today; // posted date
        patch.claimReceivedDate = today;
      }
      if (newValue === NEW_PENDING_PAYMENT && !record.approvedDate) {
        fields.approvedDate = today;
        patch.approvedDate = today;
      }
      // Amount auto-fill ONLY for a fresh claim (no prior amount). For an already paid/denied claim
      // the target amount stays empty → the required check below blocks (the form's "Amount Required").
      if (!hasExistingAmount && totalAmount > 0) {
        if ([PAID, NEW_PENDING_PAYMENT].includes(newValue)) {
          fields.paidAmount = totalAmount;
          patch.claimPaidAmount = totalAmount;
        }
        if (newValue === DENIED) {
          fields.deniedAmount = totalAmount;
          patch.claimDeniedAmount = totalAmount;
        }
      }
      return { fields, patch };
    }
    case "claimFollowupDate": {
      const iso = toIso(newValue);
      return { fields: { followupDate: iso }, patch: { claimFollowupDate: iso } };
    }
    case "claimSubmittedDate": {
      const iso = toIso(newValue);
      return { fields: { submittedDate: iso }, patch: { claimSubmittedDate: iso } };
    }
    case "dateReceived": {
      const iso = toIso(newValue);
      return { fields: { receivedDate: iso }, patch: { claimReceivedDate: iso } };
    }
    case "approvedDate": {
      const iso = toIso(newValue);
      return { fields: { approvedDate: iso }, patch: { approvedDate: iso } };
    }
    case "claimFacility": {
      // Backend stores facility as a model relation (id); the table row carries the name as `claimProvider`.
      const id = newValue || null;
      const facility = facilities.find((f) => String(f.id) === String(id));
      return {
        fields: { facility: id },
        patch: { claimProvider: facility?.name || null }
      };
    }
    case "checkNumber_ACH": {
      const trimmed = (newValue || "").trim() || null;
      return { fields: { checkNumber: trimmed }, patch: { checkNumber: trimmed } };
    }
    case "claimAssignee": {
      const id = newValue || null;
      const assignee = assignees.find((a) => String(a.id) === String(id));
      return {
        fields: { assignee: id },
        patch: { claimAssignee: id, claimAssigneeName: assignee?.displayName || null }
      };
    }
    case "enrollmentAssignee": {
      // Lives on the application — uses the enrollment endpoint, not the claim endpoint.
      const id = newValue || null;
      const assignee = assignees.find((a) => String(a.id) === String(id));
      return {
        fields: { assignee: id },
        patch: { enrollmentAssignee: id, enrollmentAssigneeName: assignee?.displayName || null }
      };
    }
    default:
      return null;
  }
};

const useInlineEditClaim = (lookups = {}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const currentUser = useSelector(selectUser);
  const filtersModified = useSelector(selectFiltersModified);
  const { trackInlineEditTriggered, trackInlineEditCompleted } = useInlineEditTracking();

  const commit = useCallback(
    async (record, columnKey, newValue, view = selectedViews.allClaims) => {
      const plan = buildPlanForClaim(record, columnKey, newValue, lookups);
      if (!plan) return { ok: false };

      // Claim & enrollment assignee are required (track-tab parity) — never persist empty.
      if ((columnKey === "claimAssignee" || columnKey === "enrollmentAssignee") && !plan.fields.assignee) {
        ErrorMessage(
          columnKey === "claimAssignee"
            ? t("patientApplications.claims_tab.error_message.assignee_required_error")
            : t("patientApplications.assignee_required_error")
        );
        return { ok: false };
      }

      // Claim-form parity: a status change (e.g. → Denied) shouldn't leave the target status's
      // required fields empty (denied date / denied amount) — block and prompt to fill them.
      if (columnKey === "claimStatus" && getMissingClaimFields(newValue, record, plan).length > 0) {
        ErrorMessage(t("inline_edit.required_fields_for_status"));
        return { ok: false };
      }

      const claimId = record.claimId || record.id;
      const { patientId } = record;
      if (!claimId || !patientId) {
        ErrorMessage(t("inline_edit.error_toast"));
        return { ok: false };
      }

      trackInlineEditTriggered({ attribute: columnKey });
      const startedAt = typeof performance !== "undefined" ? performance.now() : Date.now();
      try {
        let finalPatch = plan.patch;
        if (columnKey === "enrollmentAssignee") {
          const { papId, applicationId } = record;
          if (!papId || !applicationId) {
            ErrorMessage(t("inline_edit.error_toast"));
            return { ok: false };
          }
          // Enrollment assignee lives on the application — leaves claim.updatedBy (Last saved by) as-is.
          await updateEnrollmentInlineAPI(patientId, papId, applicationId, plan.fields);
        } else {
          await updateClaimInlineAPI(claimId, patientId, plan.fields);
          // Backend stamps claim.updatedBy = current user; mirror it so "Last saved by" refreshes
          // instantly (the column resolves the id to a name) instead of waiting for a refetch.
          if (currentUser?.id) {
            finalPatch = { ...plan.patch, claimLastSavedBy: currentUser.id };
          }
        }
        dispatch(ACTION.updateClaimsInPlace(view, [{ ...record, ...finalPatch }]));
        // Active filters: the edited row may no longer match, so re-run the search (backend
        // applies filters). Skipped when no filter is set, to avoid a needless fetch.
        if (filtersModified) {
          dispatch(ACTION.setTableDataAction(null, view));
        }
        SuccessMessage(t("inline_edit.success_toast"));
        trackInlineEditCompleted({
          attribute: columnKey,
          status: InlineEditStatus.SUCCESS,
          runtimeMs: Math.round((typeof performance !== "undefined" ? performance.now() : Date.now()) - startedAt)
        });
        return { ok: true };
      } catch (error) {
        ErrorMessage(t("inline_edit.error_toast"));
        trackInlineEditCompleted({
          attribute: columnKey,
          status: InlineEditStatus.FAILURE,
          runtimeMs: Math.round((typeof performance !== "undefined" ? performance.now() : Date.now()) - startedAt)
        });
        return { ok: false, error };
      }
    },
    [dispatch, t, currentUser, filtersModified, trackInlineEditTriggered, trackInlineEditCompleted, lookups]
  );

  return { commit };
};

export default useInlineEditClaim;
